import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, where, getDocs, doc, getDoc, setDoc } from '../firebaseConfig';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import Card from '@mui/material/Card';
import MDBox from '../components/MDBox';
import Divider from '@mui/material/Divider';
import TimelineIcon from '@mui/icons-material/Timeline';
import MaterialTooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import MDTypography from '../components/MDTypography';
import { useTheme } from '@mui/material/styles';
import { usePeriod } from '../components/PeriodContext';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const getWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  return Math.ceil(((d - new Date(d.getFullYear(), 0, 1)) / 86400000 + 1) / 7);
};

const TeamRRRatioTrendOverall = ({ teamId, userIDs }) => {
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const [monthlyRRRatios, setMonthlyRRRatios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    const fetchTeamRRRatios = async () => {
      setLoading(true);
      if (!teamId || !userIDs || userIDs.length === 0) {
        console.log('TeamRRRatioTrendOverall: Missing teamId or userIDs', { teamId, userIDs });
        setMonthlyRRRatios([]);
        setHasData(false);
        setLoading(false);
        return;
      }

      try {
        const oneEightyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 180))
          .toISOString()
          .split('T')[0]; // e.g., "2025-02-21"
        const sevenDaysAgo = new Date(new Date().setDate(new Date().getDate() - 7));

        // Aggregate RR ratios across all users
        let aggregatedMonthlyRRRatios = Array(6).fill(0);
        let userCountWithData = 0;

        for (const userId of userIDs) {
          const userDataRef = doc(db, 'teamData', teamId, 'users', userId);
          const userDataDoc = await getDoc(userDataRef);

          let monthlyRRRatios = Array(6).fill(0);
          let hasCachedData = false;

          if (userDataDoc.exists()) {
            const userData = userDataDoc.data();
            if (userData.lastUpdated?.toDate() >= sevenDaysAgo && userData.monthlyRRRatios) {
              monthlyRRRatios = userData.monthlyRRRatios;
              hasCachedData = true;
              console.log(`TeamRRRatioTrendOverall: Using cached RR ratios for user ${userId}:`, monthlyRRRatios);
            }
          }

          if (!hasCachedData) {
            let allTrades = [];
            const accountsQuery = query(collection(db, `Users/${userId}/tradeAccounts`));
            const accountsDocs = await getDocs(accountsQuery);
            const tradeAccountIds = accountsDocs.docs.map((doc) => doc.id).concat('default');

            for (const accountId of tradeAccountIds) {
              const tradesQuery = query(
                collection(db, `Users/${userId}/tradeAccounts/${accountId}/trades`),
                where('status', '==', showMissedTrades ? 'Missed' : 'Closed'),
                where('date', '>=', oneEightyDaysAgo)
              );
              const tradesSnapshot = await getDocs(tradesQuery);
              const tradesData = tradesSnapshot.docs
                .map((doc) => {
                  const data = doc.data();
                  return {
                    id: doc.id,
                    date: data.date,
                    entry: parseFloat(data.entry) || 0,
                    sl: parseFloat(data.sl) || 0,
                    tp: parseFloat(data.tp) || 0,
                    tags: Array.isArray(data.tags) ? data.tags : [],
                    raw: { entry: data.entry, sl: data.sl, tp: data.tp },
                  };
                })
                .filter((trade) => {
                  if (!trade.date || !/^\d{4}-\d{2}-\d{2}$/.test(trade.date)) {
                    console.warn(`Invalid date format for trade ${trade.id}:`, trade.date);
                    return false;
                  }
                  const tradeDate = new Date(trade.date);
                  if (isNaN(tradeDate.getTime())) {
                    console.warn(`Invalid date object for trade ${trade.id}:`, trade.date);
                    return false;
                  }
                  if (trade.entry === 0 || trade.sl === 0 || trade.tp === 0) {
                    console.warn(`Invalid trade data for trade ${trade.id}:`, trade.raw);
                    return false;
                  }
                  if (Math.abs(trade.sl - trade.entry) < 0.00001) {
                    console.warn(`SL too close to entry for trade ${trade.id}:`, trade.raw);
                    return false;
                  }
                  return true;
                });
              allTrades = allTrades.concat(tradesData);
            }

            // Apply period and tag filters
            const filteredTrades = filterTrades(allTrades);

            // Aggregate by month
            const monthlyRisk = Array(6).fill(0);
            const monthlyReward = Array(6).fill(0);
            const monthlyCounts = Array(6).fill(0);

            filteredTrades.forEach((trade) => {
              const tradeDate = new Date(trade.date);
              const monthIndex = Math.floor((new Date() - tradeDate) / (1000 * 60 * 60 * 24 * 30));
              if (monthIndex >= 0 && monthIndex < 6) {
                const risk = Math.abs(trade.entry - trade.sl);
                const reward = Math.abs(trade.tp - trade.entry);
                monthlyRisk[monthIndex] += risk;
                monthlyReward[monthIndex] += reward;
                monthlyCounts[monthIndex]++;
              }
            });

            monthlyRRRatios = monthlyRisk.map((risk, idx) =>
              monthlyCounts[idx] > 0 && risk > 0 ? (monthlyReward[idx] / risk).toFixed(2) : 0
            );

            await setDoc(userDataRef, {
              monthlyRRRatios,
              lastUpdated: new Date(),
              period: 'past 180 days',
            }, { merge: true });
            console.log(`TeamRRRatioTrendOverall: Stored new RR ratios for user ${userId} in teamData/${teamId}/users/${userId}:`, monthlyRRRatios);
          }

          // Aggregate for team-wide display
          if (monthlyRRRatios.some(ratio => parseFloat(ratio) > 0)) {
            userCountWithData++;
            monthlyRRRatios.forEach((ratio, idx) => {
              aggregatedMonthlyRRRatios[idx] += parseFloat(ratio);
            });
          }
        }

        // Average across users
        const teamMonthlyRRRatios = aggregatedMonthlyRRRatios.map((sum) =>
          userCountWithData > 0 ? (sum / userCountWithData).toFixed(2) : 0
        );

        setMonthlyRRRatios(teamMonthlyRRRatios);
        setHasData(teamMonthlyRRRatios.some(ratio => parseFloat(ratio) > 0));
        console.log('TeamRRRatioTrendOverall: Aggregated team RR ratios:', teamMonthlyRRRatios);
      } catch (error) {
        console.error('TeamRRRatioTrendOverall: Error fetching trades:', error);
        setMonthlyRRRatios([]);
        setHasData(false);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamRRRatios();
  }, [teamId, userIDs, showMissedTrades, selectedPeriod, selectedYear, selectedMonth, selectedTag]);

  const filterTrades = (trades) => {
    return trades.filter((trade) => {
      const tradeDate = trade.date ? new Date(trade.date) : null;
      if (!tradeDate || isNaN(tradeDate.getTime())) return false;
      const matchesPeriod = () => {
        if (selectedPeriod === 'today') {
          return tradeDate.toDateString() === new Date().toDateString();
        } else if (selectedPeriod === 'this-week') {
          return getWeek(tradeDate) === getWeek(new Date()) && tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'this-month') {
          return tradeDate.getMonth() === new Date().getMonth() && tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'this-year') {
          return tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'pick-month') {
          return tradeDate.getMonth() + 1 === selectedMonth && tradeDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-year') {
          return tradeDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-month-year') {
          return tradeDate.getMonth() + 1 === selectedMonth && tradeDate.getFullYear() === selectedYear;
        }
        return true; // all-time
      };
      const matchesTag = selectedTag === 'all' || (Array.isArray(trade.tags) && trade.tags.includes(selectedTag));
      return matchesPeriod() && matchesTag;
    });
  };

  const getMonthLabels = () => {
    const labels = [];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      labels.push(date.toLocaleString('default', { month: 'short', year: 'numeric' })); // e.g., "Dec 2024"
    }
    return labels;
  };

  const data = {
    labels: getMonthLabels(),
    datasets: [
      {
        label: 'Team RR Ratio',
        data: monthlyRRRatios,
        fill: false,
        backgroundColor: 'rgb(75, 192, 192)',
        borderColor: 'rgba(75, 192, 192, 0.2)',
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: { ticks: { autoSkip: true, maxTicksLimit: 6, maxRotation: 45, minRotation: 45 } },
      y: { beginAtZero: true, min: 0, max: 5, ticks: { stepSize: 0.5 } },
    },
    plugins: {
      legend: { display: true },
      tooltip: { callbacks: { label: (context) => `RR Ratio: ${context.parsed.y.toFixed(2)}` } },
    },
  };

  const tooltipContent = 'Team Risk-Reward Ratio shows the average reward-to-risk ratio across all team members’ trades over the past 180 days, aggregated monthly, updated weekly.';

  if (loading) {
    return (
      <Card style={{ minHeight: '300px' }}>
        <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
          <MDTypography variant="caption" color="text">
            Loading trades...
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  if (!hasData) {
    return (
      <Card style={{ minHeight: '300px' }}>
        <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
          <MDBox display="flex" justifyContent="space-between">
            <MDBox
              variant="gradient"
              bgColor="light"
              color="dark"
              coloredShadow="light"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="4rem"
              height="4rem"
              mt={-3}
            >
              <TimelineIcon fontSize="medium" color="inherit" />
            </MDBox>
            <MDBox textAlign="right" lineHeight={1.25}>
              <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
                Team Risk-Reward Ratio Trend (All Members & Accounts)
              </MDTypography>
              <MaterialTooltip
                title={tooltipContent}
                placement="bottom-end"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      border: `1px solid ${theme.palette.divider}`,
                      p: 1.5,
                      maxWidth: '250px',
                      fontSize: '0.75rem',
                      lineHeight: 1.5,
                      '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                    },
                  },
                }}
              >
                <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
              </MaterialTooltip>
            </MDBox>
          </MDBox>
          <Divider />
          <MDBox
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
            textAlign="center"
            px={2}
          >
            <InfoIcon sx={{ fontSize: 40, mb: 2, color: iconColor }} />
            <MDTypography variant="h6" color="text">
              No RR ratio data available
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Team members need to complete trades with valid entry, SL, and TP to see RR analysis
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    );
  }

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <TimelineIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Team Risk-Reward Ratio Trend (All Members & Accounts)
            </MDTypography>
            <MaterialTooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                  },
                },
              }}
            >
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </MaterialTooltip>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox height="100%">
          <Line data={data} options={chartOptions} />
        </MDBox>
      </MDBox>
    </Card>
  );
};

TeamRRRatioTrendOverall.propTypes = {
  teamId: PropTypes.string.isRequired,
  userIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default TeamRRRatioTrendOverall;